import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon.tsx';
import {
	getChatPanelLabel,
	shouldShowBackChevron,
} from '@/spaces/chat/panels/panelLabel';
import { trackEvent } from '@/utils/fullstory';

import { stepsForAction } from '../../../../../bridge/types/setup-steps.ts';
import type { ActionChatPanel } from '../../../../../bridge/types/state.ts';

interface MonitorActionPanelProps {
	panel: ActionChatPanel;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const MonitorActionPanel = ({
	panel,
	onBack,
	onClose,
	style,
}: MonitorActionPanelProps) => {
	const bridgeApi = useBridgeApi();
	const steps = stepsForAction(panel.action_type ?? '');
	const currentStep = panel.step_index ?? 0;
	const stepName = steps[currentStep] ?? 'Configuration';

	const history = bridgeApi.user_state?.chat_panel_history ?? [];
	const defaultBack = useCallback(() => {
		bridgeApi.sendAction({ type: 'do_pop_chat_panel' });
	}, [bridgeApi]);
	// Through shouldShowBackChevron, like ChatPanel and ChatPanelLayout. A raw
	// length test counts an entry the pop reducer refuses to restore, such as a
	// saved inspiration, so the chevron drew and pressing it did nothing.
	const showChevron = shouldShowBackChevron(history);
	const resolvedBack = onBack ?? (showChevron ? defaultBack : undefined);
	const resolvedBackLabel = showChevron
		? getChatPanelLabel(history[history.length - 1])
		: undefined;

	const handleNext = () => {
		const isFinalStep = currentStep >= steps.length - 1;
		trackEvent('Monitor Action Step Completed', {
			action_id: panel.item_id,
			step_name: stepName,
			is_final_step: isFinalStep,
		});
		if (!isFinalStep) {
			bridgeApi.sendAction({ type: 'do_panel_step_next' });
		} else {
			const snapshots = bridgeApi.user_state?.process_setup_snapshots ?? {};
			for (const snapshot of Object.values(snapshots)) {
				if (!snapshot) continue;
				if (snapshot.actions.some((a) => a.id === panel.item_id)) {
					bridgeApi.sendAction({
						type: 'do_mark_setup_item_completed',
						process_id: snapshot.process_id,
						item_id: panel.item_id,
					});
					break;
				}
			}
			onClose();
		}
	};

	const handleBack = () => {
		if (currentStep > 0) {
			trackEvent('Monitor Action Step Back Clicked', {
				action_id: panel.item_id,
				step_name: stepName,
			});
			bridgeApi.sendAction({ type: 'do_panel_step_back' });
		}
	};

	return (
		<div
			className="flex h-full flex-col overflow-hidden bg-white"
			style={style}
		>
			<div
				className="flex items-center justify-between gap-2 border-b px-4 py-3"
			>
				<div className="flex min-w-0 flex-1 items-center gap-2">
					{resolvedBack && (
						<button
							type="button"
							onClick={resolvedBack}
							aria-label={
								resolvedBackLabel ? `Back to ${resolvedBackLabel}` : 'Back'
							}
							title={
								resolvedBackLabel ? `Back to ${resolvedBackLabel}` : 'Back'
							}
							className="-ml-1 flex shrink-0 items-center rounded-md p-1
								text-gray-500 transition-colors hover:bg-gray-100
								hover:text-gray-700"
						>
							<ChevronLeftIcon className="size-5 shrink-0" />
						</button>
					)}
					<h2 className="truncate text-sm font-semibold">{stepName}</h2>
				</div>
				<button
					type="button"
					className="text-gray-400 hover:text-gray-600"
					onClick={onClose}
				>
					✕
				</button>
			</div>

			<div className="flex items-center gap-1 border-b px-4 py-2">
				{steps.map((step, i) => (
					<div
						key={step}
						className={`h-1 flex-1 rounded-full
						${i <= currentStep ? 'bg-blue-500' : 'bg-gray-200'}`}
					/>
				))}
			</div>

			<div className="scrollbar-hidden flex-1 overflow-y-auto p-4">
				{stepName === 'MonitorCondition' && (
					<div>
						<p className="mb-2 text-sm font-medium text-gray-700">
							Match Condition
						</p>
						<p className="text-xs text-gray-500">
							Configure the condition that triggers this monitor and the media
							scope it watches.
						</p>
						<div
							className="mt-4 rounded-md border border-dashed border-gray-300
								p-6 text-center"
						>
							<p className="text-xs text-gray-400">
								Condition editor will be wired here.
							</p>
						</div>
					</div>
				)}
				{stepName === 'MonitorResponse' && (
					<div>
						<p className="mb-2 text-sm font-medium text-gray-700">
							Response Configuration
						</p>
						<p className="text-xs text-gray-500">
							Configure the response type, reply/DM prompts, and capture
							segment.
						</p>
						<div
							className="mt-4 rounded-md border border-dashed border-gray-300
								p-6 text-center"
						>
							<p className="text-xs text-gray-400">
								Response config UI will be wired here.
							</p>
						</div>
					</div>
				)}
				{stepName === 'GenericActionConfig' && (
					<div
						className="rounded-md border border-dashed border-gray-300 p-6
							text-center"
					>
						<p className="text-xs text-gray-400">Action configuration panel.</p>
					</div>
				)}
			</div>

			<div className="flex gap-2 border-t px-4 py-3">
				{currentStep > 0 && (
					<button
						type="button"
						className="rounded-md border px-3 py-2 text-sm font-medium
							text-gray-700 hover:bg-gray-50"
						onClick={handleBack}
					>
						Back
					</button>
				)}
				<button
					type="button"
					className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium
						text-white hover:bg-blue-700"
					onClick={handleNext}
				>
					{currentStep < steps.length - 1 ? 'Next' : 'Done'}
				</button>
			</div>
		</div>
	);
};
